import { useState } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { Badge, ListPage } from "../../components/crud/ListPage"
import { Checkbox, DateInput, NumberInput, Select, TextareaInput, TextInput } from "../../components/forms/Inputs"
import { postalCodeToCommune } from "../../hooks/helpers"
import { useToday } from "../../hooks/useToday"
import { useTranslations } from "../../layers/Localization"
import { ArticlesList } from "./Articles"
import { TvaInput, usePaysOptions, useRegimeTvaOptions } from "./Clients"
import { useLangueOptions } from "./Gerants"
import { HistoriquesList } from "./Historique"
import { PersonnelList } from "./Personnel"
import { Popup } from "../../components/layout/Popup"
import { useMoisOptions } from "./FacturesAchat"
import { useObjectState } from "../../hooks/useObjectState"
import { Form } from "../../components/forms/Form"
import { Button } from "../../components/layout/Button"
import { FaBan, FaFileInvoiceDollar } from "react-icons/fa"

const useTypeFournisseurOptions = () => {
    const t = useTranslations()
    return [
        { value: 'MATERIEL', label: t('Matériel-produits'), color: "#10B981" },
        { value: 'SOUS_TRAITANT', label: t('Sous-traitant'), color: "#8B5CF6" },
        { value: 'SERVICES', label: t('Services'), color: "#06B6D4" },
        { value: 'FOURNITURES', label: t('Fournitures'), color: "#F97316" },
        { value: 'AUTRES', label: t('Autres'), color: "#64748B" },
    ]
}

const useActions = ({ setAFacturerId }) => {
    const t = useTranslations()
    const today = useToday()
    return {
        [t('Aperçu facturation')]: item => setAFacturerId(item.id),
        [t('Aperçu prestations')]: x => window.open(`${process.env.REACT_APP_API_URL}/fournisseurs/${x.id}/prestations?date=${encodeURIComponent(today.toISOString())}`, '_blank').focus()
    }
}

function FourniseurFields({ item, set, tab }) {
    const t = useTranslations()
    const typeOptions = useTypeFournisseurOptions()
    const langueOptions = useLangueOptions()
    const paysOptions = usePaysOptions()
    const regimeTvaOptions = useRegimeTvaOptions()

    const onCodePostalChange = value => {
        set('cp')(value)
        set('commune')(postalCodeToCommune(value))
    }

    return <>
        {tab === t('General') && <>
            <Row>
                <TextInput className="grow" label={t("Nom")} value={item.nom} onChange={set('nom')} required />
                <TvaInput label={t("Numéro de TVA")} value={item.numTva} onChange={set('numTva')} />
                <Select label={t("Type")} value={item.type} onChange={set('type')} options={typeOptions} required />
                <Select label={t("Langue")} value={item.langue} onChange={set('langue')} options={langueOptions} required />
            </Row>
            <Row>
                <TextInput className="grow" label={t("Adresse")} value={item.adresse} onChange={set('adresse')} />
                <TextInput label={t("Code postal")} value={item.cp} onChange={onCodePostalChange} />
                <TextInput label={t("Commune")} value={item.commune} onChange={set('commune')} />
            </Row>
            <hr />
            <Row>
                <TextInput className="grow" label={t("Nom délégué")} value={item.nomDelegue} onChange={set('nomDelegue')} />
                <TextInput className="grow" label={t("Téléphone")} value={item.tel} onChange={set('tel')} />
                <TextInput className="grow" label={t("Email")} value={item.email} onChange={set('email')} />
            </Row>
            <hr />
            <Row>
                <DateInput label={t("Date de premier achat")} value={item.datePremierAchat} onChange={set('datePremierAchat')} />
                <DateInput label={t("Date de dernier achat")} value={item.dateDernierAchat} onChange={set('dateDernierAchat')} />
                <Checkbox center label={t("Archivés")} value={item.archive} onChange={set('archive')} />
            </Row>
            <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={item.memoInterne} onChange={set('memoInterne')} />
        </>}
        {tab === t('Facturation') && <>
            <Row>
                <Select label={t("Régime TVA")} value={item.regimeTva} onChange={set('regimeTva')} required options={regimeTvaOptions} />
                <Select label={t("Pays Popsy")} value={item.paysPopsy} onChange={set('paysPopsy')} options={paysOptions} required />
            </Row>
        </>}
    </>
}

export function FourniseursList(props) {
    const [aFacturerId, setAFacturerId] = useState(false)
    const [globalAFacturer, setGlobalAFacturer] = useState(false)

    const t = useTranslations()
    const today = useToday()
    const typeOptions = useTypeFournisseurOptions()

    const actions = useActions({ setAFacturerId })

    return <>
        <ListPage model="fournisseurs" title={t("Fournisseurs")} columns={{
            [t('Nom')]: 'nom',
            [t('Type')]: { sort: 'type', value: item => <Badge value={item.type} options={typeOptions} /> },
            [t('Numéro de TVA')]: 'numTva',
            [t('Téléphone')]: 'tel',
            [t('Email')]: 'email',
        }} filter={{
            [t('Actifs')]: `or2:datePremierAchat:lte=${encodeURIComponent(today.toISOString())}&or2:datePremierAchat=&or3:dateDernierAchat:gt=${encodeURIComponent(today.toISOString())}&or3:dateDernierAchat=`,
            [t('Passés')]: `dateDernierAchat:lte=${encodeURIComponent(today.toISOString())}`,
            [t('Futurs')]: `datePremierAchat:gte=${encodeURIComponent(today.toISOString())}`,
            [t('Tous')]: ``,
        }} mainActions={{
            [t('Aperçu facturation')]: () => setGlobalAFacturer(true)
        }} actions={actions} {...props} />
        <AFacturerPopup aFacturerId={aFacturerId} setAFacturerId={setAFacturerId} globalAFacturer={globalAFacturer} setGlobalAFacturer={setGlobalAFacturer} />
    </>
}

export function FourniseurDetail(props) {
    const [aFacturerId, setAFacturerId] = useState(false)

    const t = useTranslations()
    const actions = useActions({ setAFacturerId })

    return <>
        <DetailPage model="fournisseurs" title={t("Fournisseur")} Fields={FourniseurFields} tabs={[t('General'), t('Facturation')]} tables={{
            [t("Personnel")]: <PersonnelList relation="fournisseurId" />,
            [t("Articles")]: <ArticlesList relation="fournisseurId" />,
            [t("Historique")]: <HistoriquesList relation="fournisseurId" root="." />,
        }} actions={actions} {...props} />
        <AFacturerPopup aFacturerId={aFacturerId} setAFacturerId={setAFacturerId} />
    </>
}


function AFacturerPopup({ aFacturerId, setAFacturerId, globalAFacturer, setGlobalAFacturer }) {
    const t = useTranslations()
    const defaultPayload = {
        year: new Date().getUTCFullYear(),
        month: new Date().getUTCMonth() + 1,
    }
    const [payload, setPayload, set] = useObjectState(defaultPayload)
    const moisOptions = useMoisOptions()

    const openAFacturer = async () => {
        const actionPayload = payload
        closePopup()
        if (globalAFacturer) window.open(`${process.env.REACT_APP_API_URL}/fournisseurs/a-facturer?year=${actionPayload.year}&month=${actionPayload.month}`, '_blank')
        else window.open(`${process.env.REACT_APP_API_URL}/fournisseurs/${aFacturerId}/a-facturer?year=${actionPayload.year}&month=${actionPayload.month}`, '_blank')
    }

    const closePopup = () => {
        setAFacturerId(null)
        setGlobalAFacturer?.(false)
        setPayload(defaultPayload)
    }

    return <Popup value={!!aFacturerId || !!globalAFacturer} onChange={closePopup}>
        <h2 className="font-bold text-lg mb-3">{t('Générer factures')}</h2>
        <Form onSubmit={openAFacturer} className="flex flex-col gap-3">
            <Row>
                <NumberInput label={t('Année')} value={payload.year} onChange={set('year')} required />
                <Select label={t('Mois')} value={payload.month} onChange={set('month')} required options={moisOptions} />
            </Row>
            <Row className="mt-5">
                <Button gray icon={<FaBan />} onClick={closePopup}>{t('Cancel')}</Button>
                <Button submit icon={<FaFileInvoiceDollar />}>{t('Aperçu')}</Button>
            </Row>
        </Form>
    </Popup>
}